import { config } from '../config';
import { store } from '../store';
import { validateAndRefreshToken } from '../utils/tokenUtils';
import { buildSalesPlaybookRestore } from './salesPlaybookUsageMapper';
import { fetchPlaybooks, fetchRoleData, restoreSalesPlaybookState } from '../store/slices/salesPlaybookSlice';
import type { SavedParticipantRow, SavedTranscriptRow } from '../types/sessionState';

/** A rep resuming their own call never waits longer than this for their saved record. */
export const OWN_SESSION_RECORD_TIMEOUT_MS = 10_000;

/** The parts of GET /resume the web reads. */
export interface OwnSessionRecord {
  participants?: SavedParticipantRow[];
  transcripts?: SavedTranscriptRow[];
  salesPlaybookUsage?: unknown;
}

/**
 * GET /api/conversations/sessions/:id/resume: the signed-in user's OWN saved
 * call, with their private working state (sales-playbook usage). The backend
 * reads the owner from the Bearer token (auth-gaps batch, 2026-10-03) and
 * answers `{exists: false}` to anyone else, so a watcher gets nothing here;
 * watchers read GET /api/sessions/:id/state. Null when there is no record or
 * it could not be read in `timeoutMs` (logged).
 */
export const fetchOwnSessionRecord = async (
  sessionId: string,
  timeoutMs: number = OWN_SESSION_RECORD_TIMEOUT_MS
): Promise<OwnSessionRecord | null> => {
  if (!sessionId) return null;
  const { token } = await validateAndRefreshToken();
  if (!token) {
    console.warn('[ownSessionRecord] No Firebase token: cannot read the saved call');
    return null;
  }
  const url = `${config.server.backendUrl}/api/conversations/sessions/${encodeURIComponent(sessionId)}/resume`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (!response.ok) {
      console.warn(`[ownSessionRecord] /resume answered ${response.status} for ${sessionId}`);
      return null;
    }
    const payload = await response.json();
    return payload?.exists && payload?.data ? (payload.data as OwnSessionRecord) : null;
  } catch (error) {
    console.warn(`[ownSessionRecord] Could not read the saved call ${sessionId}:`, error);
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * The owner resumes their call: put back the sales playbook they had selected,
 * its role and what they had checked. The transcript and cards come from the
 * history restore, not from here.
 */
export const restoreOwnPlaybookUsage = async (sessionId: string): Promise<void> => {
  const record = await fetchOwnSessionRecord(sessionId);
  if (!record?.salesPlaybookUsage) return;

  const playbooksResult = await store.dispatch(fetchPlaybooks());
  const playbooks = Array.isArray(playbooksResult?.payload) ? playbooksResult.payload : [];
  // The mapper is the single place the API usage shape is normalized into the
  // playbookName-keyed Redux state.
  const playbookRestore = buildSalesPlaybookRestore(record.salesPlaybookUsage, playbooks);
  if (!playbookRestore) return;

  store.dispatch(restoreSalesPlaybookState(playbookRestore.restorePayload));
  store.dispatch(
    fetchRoleData({
      playbookName: playbookRestore.restorePayload.selectedPlaybookName,
      roleName: playbookRestore.defaultRole,
    })
  );
};
